import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { siteData } from '../../../config/siteData';
import { createClient } from '@supabase/supabase-js';
import CompaniesBanner from './CompaniesBanner';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface PortfolioItem {
  id: number;
  title: string;
  location: string;
  value: string;
  image_url: string;
  category: 'Property' | 'Healthcare' | 'Investments';
  display_order: number;
  is_active: boolean;
}

export default function Portfolio() {
  const [animationKey, setAnimationKey] = useState(0);
  const [activeTab, setActiveTab] = useState<'Property' | 'Healthcare' | 'Investments'>('Property');
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const tabsRef = useRef<HTMLDivElement>(null);

  const itemsPerPage = 3;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimationKey(prev => prev + 1);
          }
        });
      },
      { threshold: 0.3 }
    );

    const section = document.getElementById('portfolio');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  useEffect(() => {
    fetchPortfolioItems();
  }, []);

  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab]);

  const fetchPortfolioItems = async () => {
    try {
      const { data, error } = await supabase
        .from('portfolio_items')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setPortfolioItems(data || []);
    } catch (error) {
      console.error('Error fetching portfolio items:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = portfolioItems.filter(item => item.category === activeTab);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    if (tabsRef.current) {
      const tabsPosition = tabsRef.current.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: tabsPosition, behavior: 'smooth' });
    }
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : totalPages - 1));
  };

  const handleNextPage = () => {
    if (tabsRef.current) {
      const tabsPosition = tabsRef.current.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: tabsPosition, behavior: 'smooth' });
    }
    setCurrentPage((prev) => (prev < totalPages - 1 ? prev + 1 : 0));
  };

  const handlePageClick = (pageIndex: number) => {
    if (tabsRef.current) {
      const tabsPosition = tabsRef.current.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: tabsPosition, behavior: 'smooth' });
    }
    setCurrentPage(pageIndex);
  };

  return (
    <>
      <section id="portfolio" className="pt-8 sm:pt-12 lg:pt-16 pb-14 sm:pb-20 lg:pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <motion.div
            key={`portfolio-header-${animationKey}`}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8 sm:mb-10 lg:mb-12"
          >
            <div className="text-navy text-xs sm:text-sm font-serif tracking-wider mb-2 sm:mb-3">OUR PORTFOLIO</div>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-3 sm:mb-4 px-4">
              <span className="text-navy">Strategic </span>
              <span className="text-gold italic">Investment </span>
              <span className="text-navy">Holdings</span>
            </h2>
            <p className="text-text-light text-sm sm:text-base max-w-2xl mx-auto leading-relaxed px-4">
              A carefully curated selection of premium assets across our three core sectors, demonstrating our commitment to excellence and value creation.
            </p>
          </motion.div>

          {/* Tab Navigation */}
          <motion.div
            ref={tabsRef}
            key={`portfolio-tabs-${animationKey}`}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex justify-center mb-6 sm:mb-8 px-4"
          >
            <div className="inline-flex bg-cream rounded-full p-1 shadow-lg w-full max-w-xl overflow-x-auto">
              <button
                onClick={() => setActiveTab('Property')}
                className={`flex-1 px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'Property' ? 'bg-navy text-white' : 'text-navy hover:text-gold'
                }`}
              >
                Property
              </button>
              <button
                onClick={() => setActiveTab('Healthcare')}
                className={`flex-1 px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'Healthcare' ? 'bg-navy text-white' : 'text-navy hover:text-gold'
                }`}
              >
                Healthcare
              </button>
              <button
                onClick={() => setActiveTab('Investments')}
                className={`flex-1 px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'Investments' ? 'bg-navy text-white' : 'text-navy hover:text-gold'
                }`}
              >
                Investments
              </button>
            </div>
          </motion.div>

          {/* Portfolio Grid with Carousel */}
          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-navy border-t-gold"></div>
            </div>
          ) : (
            <div className="relative px-4 sm:px-0">
              {/* Navigation Arrows */}
              {filteredItems.length > itemsPerPage && (
                <>
                  <button
                    onClick={handlePrevPage}
                    className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-navy hover:bg-gold text-white w-10 h-10 rounded-full shadow-lg transition-all duration-300 items-center justify-center cursor-pointer group"
                    aria-label="Previous page"
                  >
                    <i className="ri-arrow-left-s-line text-xl group-hover:scale-110 transition-transform"></i>
                  </button>
                  <button
                    onClick={handleNextPage}
                    className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-navy hover:bg-gold text-white w-10 h-10 rounded-full shadow-lg transition-all duration-300 items-center justify-center cursor-pointer group"
                    aria-label="Next page"
                  >
                    <i className="ri-arrow-right-s-line text-xl group-hover:scale-110 transition-transform"></i>
                  </button>
                </>
              )}

              <motion.div
                key={`${activeTab}-${currentPage}-${animationKey}`}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
              >
                {currentItems.length > 0 ? (
                  currentItems.map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="bg-cream rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
                    >
                      <div className="relative h-44 sm:h-52 lg:h-56 overflow-hidden">
                        <img
                          src={item.image_url}
                          alt={item.title}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent"></div>
                        <div className="absolute top-3 right-3 bg-gold px-3 py-1.5 rounded-full">
                          <span className="text-navy font-bold text-xs">{item.value}</span>
                        </div>
                      </div>
                      <div className="p-4 sm:p-5">
                        <h3 className="text-navy text-base sm:text-lg font-bold mb-1.5 break-words">{item.title}</h3>
                        <div className="flex items-center text-text-light text-xs">
                          <i className="ri-map-pin-line mr-1.5 flex-shrink-0"></i>
                          <span className="break-words">{item.location}</span>
                        </div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-10">
                    <p className="text-text-light text-sm sm:text-base">No items in this category yet.</p>
                  </div>
                )}
              </motion.div>

              {/* Page Indicators */}
              {filteredItems.length > itemsPerPage && (
                <div className="flex justify-center items-center gap-2 mt-6 sm:mt-8">
                  {Array.from({ length: totalPages }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => handlePageClick(index)}
                      className={`transition-all duration-300 cursor-pointer touch-manipulation ${
                        currentPage === index
                          ? 'w-10 h-10 bg-navy/20 rounded-full flex items-center justify-center'
                          : 'w-10 h-10 bg-navy/10 hover:bg-navy/20 rounded-full flex items-center justify-center'
                      }`}
                      aria-label={`Go to page ${index + 1}`}
                      type="button"
                    >
                      <span className={`transition-all duration-300 rounded-full ${
                        currentPage === index
                          ? 'w-2.5 h-2.5 bg-navy'
                          : 'w-1.5 h-1.5 bg-navy/40'
                      }`}></span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Portfolio Stats */}
          <motion.div
            key={`portfolio-stats-${animationKey}`}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-8 sm:mt-10 lg:mt-12 bg-navy rounded-2xl p-6 sm:p-8 lg:p-10"
          >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 text-center">
              <div>
                <div className="text-gold text-2xl sm:text-3xl lg:text-4xl font-serif font-bold mb-1 break-words">{siteData.totalPortfolioValue}</div>
                <div className="text-white/80 text-xs">Total Portfolio Value</div>
              </div>
              <div>
                <div className="text-gold text-2xl sm:text-3xl lg:text-4xl font-serif font-bold mb-1 break-words">{siteData.activeInvestments}</div>
                <div className="text-white/80 text-xs">Active Investments</div>
              </div>
              <div>
                <div className="text-gold text-2xl sm:text-3xl lg:text-4xl font-serif font-bold mb-1 break-words">{siteData.averageAnnualReturn}</div>
                <div className="text-white/80 text-xs">Average Annual Return</div>
              </div>
              <div>
                <div className="text-gold text-2xl sm:text-3xl lg:text-4xl font-serif font-bold mb-1 break-words">{siteData.clientSatisfaction}</div>
                <div className="text-white/80 text-xs">Client Satisfaction</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      <CompaniesBanner />
    </>
  );
}
